import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { debtorService } from '../services/debtorService';
import { exportService } from '../services/exportService';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AddDebtorModal from '../components/debtor/AddDebtorModal';
import EditDebtorModal from '../components/debtor/EditDebtorModal';
import DeleteDebtorModal from '../components/debtor/DeleteDebtorModal';
import BulkImportModal from '../components/debtor/BulkImportModal';
import {
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiAlertCircle,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPrinter,
  FiDownload,
  FiSearch,
  FiFilter
} from 'react-icons/fi';

const CustomersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(location.state?.search || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('lastUpdatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, sortBy, sortOrder, currentPage]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await debtorService.getAll({
        search: searchTerm,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: 20
      });
      setCustomers(response.data.debtors || []);
      setSummary(response.data.summary || null);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      showError('Failed to load customers');
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setShowEditModal(true);
  };

  const handleDelete = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  };

  const handleView = (customer) => {
    navigate(`/debtors/${customer._id}`);
  };

  const handlePrint = (customer) => {
    // Implement print functionality
    window.print();
  };

  const handleExportExcel = async () => {
    try {
      await exportService.exportDebtorsExcel({ search: searchTerm });
      showSuccess('Excel file downloaded successfully!');
      setShowExportMenu(false);
    } catch (error) {
      showError(error.message || 'Failed to export');
    }
  };

  const handleExportCSV = async () => {
    try {
      await exportService.exportDebtorsCSV({ search: searchTerm });
      showSuccess('CSV file downloaded successfully!');
      setShowExportMenu(false);
    } catch (error) {
      showError(error.message || 'Failed to export');
    }
  };

  const isBasicPlus = user?.subscription?.package === 'basic_plus';

  if (loading && customers.length === 0) {
    return (
      <Layout title="Customers">
        <LoadingSpinner />
      </Layout>
    );
  }

  const activeCustomers = customers.filter(c => c.totalOutstanding > 0).length;
  const highRiskCustomers = summary ? customers.filter(
    c => c.totalOutstanding >= (user?.settings?.thresholdAmount || 10)
  ).length : 0;

  return (
    <Layout title="Customers List">
      {/* Statistics Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiUsers}
            value={summary.totalDebtors}
            label="Total Customers"
            bgColor="bg-pastel-lavender"
            iconColor="text-white"
            iconBgColor="bg-primary-500"
          />
          <StatCard
            icon={FiDollarSign}
            value={`BHD ${summary.totalOutstandingAmount.toFixed(3)}`}
            label="Total Outstanding"
            bgColor="bg-pastel-pink"
            iconColor="text-white"
            iconBgColor="bg-danger-500"
          />
          <StatCard
            icon={FiTrendingUp}
            value={activeCustomers}
            label="Active Customers"
            bgColor="bg-pastel-mint"
            iconColor="text-white"
            iconBgColor="bg-success-500"
          />
          <StatCard
            icon={FiAlertCircle}
            value={highRiskCustomers}
            label="High Risk Alerts"
            bgColor="bg-pastel-peach"
            iconColor="text-white"
            iconBgColor="bg-warning-500"
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center mb-4">
          {/* Search */}
          <div className="flex-1 min-w-[250px] relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Customer or Building..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="text-neutral-500 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="cleared">Cleared</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Action Buttons */}
          <Button onClick={() => setShowAddModal(true)}>
            + Add New Customer
          </Button>

          {isBasicPlus && (
            <Button onClick={() => setShowBulkImportModal(true)} variant="outline">
              Bulk Import
            </Button>
          )}

          {/* Export Dropdown */}
          <div className="relative">
            <Button onClick={() => setShowExportMenu(!showExportMenu)} variant="outline">
              <FiDownload className="w-4 h-4 mr-2 inline" />
              Export
            </Button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-neutral-200 z-10 overflow-hidden">
                <button
                  onClick={handleExportExcel}
                  className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                >
                  Export to Excel
                </button>
                <button
                  onClick={handleExportCSV}
                  className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                >
                  Export to CSV
                </button>
                {isBasicPlus && (
                  <button
                    onClick={() => {/* PDF export */}}
                    className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                  >
                    Export to PDF
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex gap-2 text-sm">
          <span className="text-neutral-600">Sort by:</span>
          <button
            onClick={() => {
              setSortBy('lastUpdatedAt');
              setSortOrder('desc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'lastUpdatedAt' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Latest
          </button>
          <span className="text-neutral-300">|</span>
          <button
            onClick={() => {
              setSortBy('name');
              setSortOrder('asc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'name' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Name (A-Z)
          </button>
          <span className="text-neutral-300">|</span>
          <button
            onClick={() => {
              setSortBy('totalOutstanding');
              setSortOrder('desc');
            }}
            className={`px-2 py-1 rounded ${
              sortBy === 'totalOutstanding' ? 'text-primary-600 font-semibold' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Highest Debt
          </button>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Cust ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Outstanding
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Total Paid
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <FiUsers className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                    <p className="text-neutral-600 mb-4">
                      {searchTerm ? 'No customers found matching your search.' : 'No customers yet. Add your first customer!'}
                    </p>
                  </td>
                </tr>
              ) : (
                customers.map((customer, index) => (
                  <tr key={customer._id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 font-medium">
                      {100100 + index}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-neutral-900">{customer.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {customer.mobile}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {customer.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-semibold ${
                        customer.totalOutstanding > 0 ? 'text-danger-600' : 'text-neutral-600'
                      }`}>
                        BHD {customer.totalOutstanding.toFixed(3)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-success-600">
                        BHD {customer.totalPaid.toFixed(3)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.status === 'active' ? 'bg-success-100 text-success-800' :
                        customer.status === 'cleared' ? 'bg-primary-100 text-primary-800' :
                        'bg-neutral-100 text-neutral-800'
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleView(customer)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(customer)}
                          className="p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(customer)}
                          className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(customer)}
                          className="p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                          title="Print"
                        >
                          <FiPrinter className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
            <div className="text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddDebtorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadCustomers}
      />

      {selectedCustomer && (
        <>
          <EditDebtorModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedCustomer(null);
            }}
            debtor={selectedCustomer}
            onSuccess={loadCustomers}
          />

          <DeleteDebtorModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedCustomer(null);
            }}
            debtor={selectedCustomer}
            onSuccess={loadCustomers}
          />
        </>
      )}

      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={loadCustomers}
      />
    </Layout>
  );
};

export default CustomersPage;
